import { useEffect } from 'react';
import { Hero } from '@/components/sections/Hero';
import { CtaBlock } from '@/components/sections/CtaBlock';
import { FaqSection } from '@/components/sections/FaqSection';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { PricingCard } from '@/components/ui/PricingCard';
import { BundleCard } from '@/components/ui/BundleCard';
import { pricingGroups, bundles, paymentTerms, notIncludedInAnyTier, addOns } from '@/data/pricing';
import { pricingFaq } from '@/data/faq';
import { useSeo } from '@/lib/seo';

export default function Pricing() {
  useSeo({
    title: 'Pricing',
    description: 'Transparent, tiered pricing for logo design, website design, and website development. No hidden fees.',
  });

  useEffect(() => {
    const data = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: pricingFaq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'pricing-faq-schema';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('pricing-faq-schema');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <>
      <Hero
        eyebrow="Pricing"
        headline={<>Transparent. No gotchas.</>}
        description="Starting prices for every service. Each project gets a fixed quote after a thirty-minute discovery call - the final number lands before any work starts."
        align="centered"
      />

      <section className="px-6 md:px-12 lg:px-20 py-12 md:py-16 border-t border-border">
        <div className="mx-auto max-w-[1440px] grid md:grid-cols-4 gap-8">
          <div className="flex flex-col gap-1">
            <span className="mono-meta text-ink-muted">Split</span>
            <span className="text-ink-primary">{paymentTerms.split}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="mono-meta text-ink-muted">Currency</span>
            <span className="text-ink-primary">{paymentTerms.currency}</span>
          </div>
          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="mono-meta text-ink-muted">VAT</span>
            <span className="text-ink-primary leading-relaxed">{paymentTerms.vat}</span>
          </div>
        </div>
        <p className="mx-auto max-w-[1440px] mt-6 text-ink-muted text-sm">{paymentTerms.invoicing}</p>
      </section>

      {pricingGroups.map((group) => (
        <section key={group.serviceSlug} className="px-6 md:px-12 lg:px-20 py-16 md:py-20 border-t border-border">
          <div className="mx-auto max-w-[1440px] flex flex-col gap-10">
            <SectionHeading eyebrow="Service" title={group.title} />
            <div className="grid md:grid-cols-3 gap-6">
              {group.tiers.map((tier) => (
                <PricingCard key={tier.slug} tier={tier} />
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28 bg-surface">
        <div className="mx-auto max-w-[1440px] flex flex-col gap-6">
          <SectionHeading
            eyebrow="Bundles"
            title="Combine and save."
            description="Three packages that bundle the most common paths our clients take. Same quality, less budget."
          />
          <div className="flex flex-col">
            {bundles.map((b) => (
              <BundleCard key={b.slug} bundle={b} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="Not included in any tier" title="Things we will quote separately." />
          <ul className="flex flex-col">
            {notIncludedInAnyTier.map((item) => (
              <li key={item} className="text-ink-muted leading-relaxed py-3 border-b border-border">{item}</li>
            ))}
          </ul>
        </div>
      </section>
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28 bg-surface">
        <div className="mx-auto max-w-[1440px] flex flex-col gap-10">
          <SectionHeading eyebrow="Add-ons" title="Optional extras." description="Tack any of these on to any tier. Quoted at time of project, not before." />
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {addOns.map((addon) => (
              <article key={addon.name} className="flex flex-col gap-2 p-6 border border-border bg-bg">
                <div className="flex justify-between items-start gap-4">
                  <h3 className="font-display text-xl font-medium">{addon.name}</h3>
                  <span className="mono-meta text-accent whitespace-nowrap">{addon.priceNote}</span>
                </div>
                <p className="text-ink-secondary text-sm leading-relaxed">{addon.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <FaqSection title="How the money works." items={pricingFaq} />

      <CtaBlock />
    </>
  );
}
